import React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { CardRadio, CardToggleBasic, RadioGroup, Step } from '@gitbook/spine-react';

import { IntegrationStepWrapper } from '../../../../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../../../../navigation';
import type { IntegrationInstallationFlowProps, StepContextType } from '../../types';
import { InstallControls } from './InstallControls';
import { SelectContentCard } from './SelectContentCard';

type InstallInContentStepProps = IntegrationInstallationFlowProps & {
    onNext: (params: StepContextType<'pick_content_to_configure'>) => void;
};

/**
 * Step in the installation flow to let the user pick in which content should the
 * integration be installed
 */
export function InstallInContentStep(props: InstallInContentStepProps) {
    const { installation, integration, organizationId, onNext } = props;

    const targetType = props.integration.target;

    const { navigate } = useNavigation();
    const [selectedSiteIds, setSelectedSiteIds] = React.useState<string[]>([]);
    const [selectedSpaceIds, setSelectedSpaceIds] = React.useState<string[]>([]);

    useSetAnalyticsScreen({
        screen: 'integration_installation',
        step: 'install_in_content',
        integration: integration.name,
        organization: organizationId,
        withAccountInstallation: !!installation,
    });

    return (
        <IntegrationStepWrapper
            title={`Install ${integration.title}`}
            description={`Select where you want to install the ${integration.title} integration.`}
        >
            <Step>
                <RadioGroup className="h-full">
                    {/* Space Cards */}
                    {(targetType === api.IntegrationTarget.All ||
                        targetType === api.IntegrationTarget.Space) && (
                        <>
                            <CardRadio value="space_all">
                                <CardToggleBasic
                                    leadingIcon={Icon.AllSpaces}
                                    title="Install on every space"
                                    description="Available to use in every space once configured."
                                />
                            </CardRadio>
                            <SelectContentCard
                                value="space_selected"
                                installation={installation}
                                integration={integration}
                                organizationId={organizationId}
                                selectedContentIds={selectedSpaceIds}
                                setSelectedContentIds={setSelectedSpaceIds}
                            />
                        </>
                    )}

                    {/* Site Cards */}
                    {(targetType === api.IntegrationTarget.All ||
                        targetType === api.IntegrationTarget.Site) && (
                        <>
                            <CardRadio value="site_all">
                                <CardToggleBasic
                                    title="Install on every site"
                                    leadingIcon={Icon.AllDocsSites}
                                    description="Available to use in every site once configured."
                                />
                            </CardRadio>
                            <SelectContentCard
                                value="site_selected"
                                installation={installation}
                                integration={integration}
                                organizationId={organizationId}
                                selectedContentIds={selectedSiteIds}
                                setSelectedContentIds={setSelectedSiteIds}
                            />
                        </>
                    )}

                    <div className="self-end">
                        <InstallControls
                            organizationId={organizationId}
                            integration={integration}
                            installation={installation}
                            selectedSiteIds={selectedSiteIds}
                            selectedSpaceIds={selectedSpaceIds}
                            onSuccessInstallInContent={onNext}
                            onSuccessInstallInAllContent={() => {
                                navigate({
                                    current: { tab: 'installations', installation: undefined },
                                });
                            }}
                        />
                    </div>
                </RadioGroup>
            </Step>
        </IntegrationStepWrapper>
    );
}
